
import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { X, Plus, Info, Upload, Link, Image } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CommissionOffer } from "@/types/commission.types";
import { useCommissionImageUpload } from "@/hooks/commission/use-commission-image-upload";

// Schema for commission offer form
const offerFormSchema = z.object({
  title: z.string().min(5, "O título deve ter pelo menos 5 caracteres"),
  description: z.string().min(20, "A descrição deve ter pelo menos 20 caracteres"),
  description_comission: z.string().min(20, "As instruções devem ter pelo menos 20 caracteres"),
  price_usd: z.coerce.number().positive("O preço deve ser positivo"),
  price_eth: z.coerce.number().min(0, "O preço em ETH não pode ser negativo"),
  delivery_time_days: z.coerce.number().int().positive("O prazo deve ser maior que zero"),
  max_revision: z.coerce.number().int().min(0, "O número de revisões não pode ser negativo"),
  currency_type: z.enum(["USD", "BRL", "EUR"]),
  accept_nft: z.boolean().default(false),
  is_active: z.boolean().default(true),
  pix_key: z.string().optional(),
});

type OfferFormValues = z.infer<typeof offerFormSchema>;

interface CommissionOfferFormProps {
  defaultValues?: Partial<CommissionOffer>;
  onSubmit: (data: OfferFormValues & {
    tags: string[];
    examples_urls: string[];
    specific_commissions: string[];
  }) => void;
  isLoading: boolean;
  additionalFields?: {
    [key: string]: {
      tooltip?: React.ReactNode;
    };
  };
}

export function CommissionOfferForm({
  defaultValues,
  onSubmit,
  isLoading,
  additionalFields
}: CommissionOfferFormProps) {
  const [tags, setTags] = useState<string[]>(defaultValues?.tags || []);
  const [examples, setExamples] = useState<string[]>(defaultValues?.examples_urls || []);
  const [specificTypes, setSpecificTypes] = useState<string[]>(defaultValues?.specific_commissions || []);
  const [tagInput, setTagInput] = useState<string>("");
  const [exampleUrlInput, setExampleUrlInput] = useState<string>("");
  const [typeInput, setTypeInput] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadImage, isUploading } = useCommissionImageUpload(defaultValues?.artist_id);
  const MAX_EXAMPLES = 6;

  // Form setup
  const form = useForm<OfferFormValues>({
    resolver: zodResolver(offerFormSchema),
    defaultValues: {
      title: defaultValues?.title || "",
      description: defaultValues?.description || "",
      description_comission: defaultValues?.description_comission || "",
      price_usd: defaultValues?.price_usd || 10,
      price_eth: defaultValues?.price_eth || 0,
      delivery_time_days: defaultValues?.delivery_time_days || 7,
      max_revision: defaultValues?.max_revision || 2,
      currency_type: defaultValues?.currency_type as "USD" | "BRL" | "EUR" || "USD",
      accept_nft: defaultValues?.accept_nft || false,
      is_active: defaultValues?.is_active !== undefined ? defaultValues.is_active : true,
      pix_key: defaultValues?.pix_key || "",
    },
  });

  // Tags handlers
  const handleAddTag = () => {
    if (tagInput.trim() !== "" && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  // Examples handlers - URL
  const handleAddExampleUrl = () => {
    if (examples.length >= MAX_EXAMPLES) {
      toast.error(`Máximo de ${MAX_EXAMPLES} exemplos atingido`);
      return;
    }

    if (exampleUrlInput.trim() !== "" && !examples.includes(exampleUrlInput.trim())) {
      setExamples([...examples, exampleUrlInput.trim()]);
      setExampleUrlInput("");
    }
  };

  // Examples handlers - File Upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (examples.length + files.length > MAX_EXAMPLES) {
      toast.error(`Você pode adicionar no máximo ${MAX_EXAMPLES} exemplos`);
      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (examples.length + i >= MAX_EXAMPLES) break;
      
      const file = files[i];
      const url = await uploadImage(file);
      if (url) {
        setExamples(prev => [...prev, url]);
      }
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveExample = (example: string) => {
    setExamples(examples.filter(e => e !== example));
  };

  // Specific commission types handlers
  const handleAddType = () => {
    if (typeInput.trim() !== "" && !specificTypes.includes(typeInput.trim())) {
      setSpecificTypes([...specificTypes, typeInput.trim()]);
      setTypeInput("");
    }
  };

  const handleRemoveType = (type: string) => {
    setSpecificTypes(specificTypes.filter(t => t !== type));
  };

  // Form submission handler
  const handleFormSubmit = (values: OfferFormValues) => {
    // Validate if examples are added
    if (examples.length === 0) {
      toast.error("Adicione pelo menos um exemplo de trabalho");
      return;
    }

    // Validate NFT requirements
    if (values.accept_nft && values.price_eth <= 0) {
      toast.error("Defina um preço em ETH para comissões com NFT");
      return;
    }

    // Combine form values with arrays
    const submissionData = {
      ...values,
      tags,
      examples_urls: examples,
      specific_commissions: specificTypes,
    };

    onSubmit(submissionData);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-8">
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Título da Oferta</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Ex: Ilustração Digital Estilo Anime" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição da Oferta</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Descreva sua oferta de comissão em detalhes..." 
                    className="min-h-[100px]" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description_comission"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Instruções para Clientes</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Instruções detalhadas para os clientes sobre como solicitar sua comissão..." 
                    className="min-h-[100px]" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="currency_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Moeda Principal</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex space-x-2"
                    >
                      <div className="flex items-center space-x-2 border rounded-md p-2 flex-1 justify-center">
                        <RadioGroupItem value="USD" id="usd" />
                        <label htmlFor="usd" className="cursor-pointer">USD ($)</label>
                      </div>
                      <div className="flex items-center space-x-2 border rounded-md p-2 flex-1 justify-center">
                        <RadioGroupItem value="BRL" id="brl" />
                        <label htmlFor="brl" className="cursor-pointer">BRL (R$)</label>
                      </div>
                      <div className="flex items-center space-x-2 border rounded-md p-2 flex-1 justify-center">
                        <RadioGroupItem value="EUR" id="eur" />
                        <label htmlFor="eur" className="cursor-pointer">EUR (€)</label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="price_usd"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Preço {form.watch("currency_type") === "USD" ? "($)" : form.watch("currency_type") === "BRL" ? "(R$)" : "(€)"}
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min="0" step="0.01" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="delivery_time_days"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prazo de Entrega (dias)</FormLabel>
                  <FormControl>
                    <Input type="number" min="1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_revision"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número de Revisões Incluídas</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="pix_key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chave PIX (opcional)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="CPF, CNPJ, Email, Telefone ou Chave Aleatória" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="accept_nft"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between p-4 border rounded-md">
                    <div className="space-y-0.5">
                      <div className="flex items-center">
                        <FormLabel className="mr-2">Aceitar NFT</FormLabel>
                        {additionalFields?.accept_nft?.tooltip}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Pagamento via contrato inteligente
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {form.watch("accept_nft") && (
                <FormField
                  control={form.control}
                  name="price_eth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <span>Preço em ETH</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-[250px]">
                                Valor em Ethereum para pagamento via NFT.
                                Sugerimos usar plataformas como CoinGecko para conversão.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min="0" 
                          step="0.001"
                          placeholder="0.01" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </div>

          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between p-4 border rounded-md">
                <div className="space-y-0.5">
                  <FormLabel>Ativar Comissão</FormLabel>
                  <p className="text-sm text-muted-foreground">
                    Se desativada, não estará visível para novos clientes
                  </p>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div>
            <h3 className="font-medium mb-2">Tipos de Comissão</h3>
            <div className="flex items-center mb-3">
              <Input
                placeholder="Adicionar tipo (ex: Sketch, Full Color, Animation)"
                value={typeInput}
                onChange={(e) => setTypeInput(e.target.value)}
                className="mr-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddType();
                  }
                }}
              />
              <Button type="button" onClick={handleAddType} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {specificTypes.map((type, index) => (
                <Badge 
                  key={index} 
                  variant="outline"
                  className="flex items-center gap-1 px-2 py-1"
                >
                  {type}
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => handleRemoveType(type)} 
                  />
                </Badge>
              ))}
              {specificTypes.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhum tipo adicionado</p>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-medium mb-2">Tags</h3>
            <div className="flex items-center mb-3">
              <Input
                placeholder="Adicionar tag (ex: anime, pixel-art)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                className="mr-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button type="button" onClick={handleAddTag} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <Badge 
                  key={index} 
                  variant="secondary"
                  className="flex items-center gap-1 px-2 py-1"
                >
                  {tag}
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => handleRemoveTag(tag)} 
                  />
                </Badge>
              ))}
              {tags.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhuma tag adicionada</p>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-medium mb-2">Exemplos de Trabalhos ({examples.length}/{MAX_EXAMPLES})</h3>
            
            <Tabs defaultValue="url">
              <TabsList className="mb-3">
                <TabsTrigger value="url" className="flex items-center gap-1">
                  <Link className="h-4 w-4 mr-1" /> URL
                </TabsTrigger>
                <TabsTrigger value="upload" className="flex items-center gap-1">
                  <Upload className="h-4 w-4 mr-1" /> Upload
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="url" className="space-y-3">
                <div className="flex items-center">
                  <Input
                    placeholder="URL da imagem de exemplo"
                    value={exampleUrlInput}
                    onChange={(e) => setExampleUrlInput(e.target.value)}
                    className="mr-2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddExampleUrl();
                      }
                    }}
                  />
                  <Button 
                    type="button" 
                    onClick={handleAddExampleUrl} 
                    size="sm"
                    disabled={examples.length >= MAX_EXAMPLES}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {examples.length >= MAX_EXAMPLES && (
                  <p className="text-xs text-amber-500">
                    Máximo de {MAX_EXAMPLES} exemplos atingido
                  </p>
                )}
              </TabsContent>
              
              <TabsContent value="upload">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      multiple
                    />
                    <Button 
                      type="button" 
                      onClick={() => fileInputRef.current?.click()} 
                      variant="outline"
                      className="w-full flex items-center gap-2"
                      disabled={isUploading || examples.length >= MAX_EXAMPLES}
                    >
                      <Image className="h-4 w-4" />
                      {isUploading 
                        ? "Enviando..." 
                        : examples.length >= MAX_EXAMPLES 
                          ? `Limite de ${MAX_EXAMPLES} exemplos atingido` 
                          : "Selecionar imagens"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Formatos aceitos: JPEG, PNG, WEBP, GIF. Tamanho máximo: 5MB
                  </p>
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {examples.map((example, index) => (
                <Card key={index} className="overflow-hidden">
                  <div className="relative">
                    <img 
                      src={example} 
                      alt={`Exemplo ${index + 1}`} 
                      className="object-cover w-full h-40"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://placehold.co/300x200?text=Imagem+Inválida';
                      }}
                    />
                    <Button 
                      type="button" 
                      size="icon" 
                      variant="destructive"
                      className="absolute top-1 right-1 h-6 w-6"
                      onClick={() => handleRemoveExample(example)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="p-2">
                    <p className="text-xs truncate">{example}</p>
                  </div>
                </Card>
              ))}
              {examples.length === 0 && (
                <div className="col-span-full">
                  <p className="text-sm text-muted-foreground">Nenhum exemplo adicionado</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <Button 
          type="submit" 
          disabled={isLoading || isUploading} 
          className="w-full md:w-auto bg-green-500 hover:bg-green-600"
        >
          {isLoading || isUploading ? 'Salvando...' : defaultValues?.id ? 'Atualizar Comissão' : 'Criar Comissão'}
        </Button>
      </form>
    </Form>
  );
}
